import type { TFunction } from 'i18next';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import type { ColumnRenderProps } from 'react-table';

import type { IColumn } from '@feathr/components';
import { Pill, TableColumnHeader, Time } from '@feathr/components';
import { TimeFormat } from '@feathr/hooks';

import type { ISampleForm } from '../sampleData';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as shellStyles from '../Shell/PrototypeShell.css';

export type TFormRowAction = 'edit' | 'duplicate' | 'rename' | 'delete';

interface IFormColumnsArgs {
  base: string;
  onAction: (form: ISampleForm, action: TFormRowAction) => void;
  renderActions: (
    form: ISampleForm,
    onAction: (f: ISampleForm, a: TFormRowAction) => void,
  ) => JSX.Element;
  t: TFunction;
}

function submissionRate(form: ISampleForm): string {
  if (form.views === 0) {
    return '—';
  }
  return numeral(form.submissions / form.views).format('0.0%');
}

function formColumns({
  base,
  onAction,
  renderActions,
  t,
}: IFormColumnsArgs): Array<IColumn<ISampleForm>> {
  return [
    {
      accessor: 'name',
      id: 'name',
      Header: TableColumnHeader({ title: t('Name'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      minWidth: 280,
      Cell({ original }): JSX.Element {
        return <Link to={base}>{original.name}</Link>;
      },
      Footer(): JSX.Element {
        return <strong>{t('Page totals')}</strong>;
      },
    },
    {
      accessor: 'status',
      id: 'status',
      Header: TableColumnHeader({ title: t('Status'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 120,
      Cell({ original }): JSX.Element {
        const isPublished = original.status === 'published';
        return (
          <Pill theme={isPublished ? 'green' : 'neutral'}>
            {isPublished ? t('Published') : t('Draft')}
          </Pill>
        );
      },
    },
    {
      accessor: 'views',
      id: 'views',
      Header: TableColumnHeader({ title: t('Views'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 100,
      Cell({ original }): JSX.Element {
        return <>{numeral(original.views).format('0,0')}</>;
      },
      Footer({ data }: ColumnRenderProps<{ _original: ISampleForm }>): JSX.Element {
        const total = data.reduce((subtotal, row) => subtotal + row._original.views, 0);
        return <>{numeral(total).format('0,0')}</>;
      },
    },
    {
      accessor: 'submissions',
      id: 'submissions',
      Header: TableColumnHeader({ title: t('Submissions'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 130,
      Cell({ original }): JSX.Element {
        return <>{numeral(original.submissions).format('0,0')}</>;
      },
      Footer({ data }: ColumnRenderProps<{ _original: ISampleForm }>): JSX.Element {
        const total = data.reduce((subtotal, row) => subtotal + row._original.submissions, 0);
        return <>{numeral(total).format('0,0')}</>;
      },
    },
    {
      accessor: (row): number => (row.views === 0 ? -1 : row.submissions / row.views),
      id: 'rate',
      Header: TableColumnHeader({ title: t('Rate'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 100,
      Cell({ original }): JSX.Element {
        return <>{submissionRate(original)}</>;
      },
    },
    {
      accessor: 'lastUpdated',
      id: 'lastUpdated',
      Header: TableColumnHeader({ title: t('Last updated'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellCenter,
      sortable: true,
      width: 160,
      Cell({ original }): JSX.Element {
        return <Time format={TimeFormat.shortDate} timestamp={original.lastUpdated} />;
      },
    },
    {
      id: 'actions',
      Header: TableColumnHeader({ title: '' }),
      width: 56,
      sortable: false,
      className: `${tableStyles.cellCenter} ${shellStyles.actionsCell}`,
      Cell({ original }): JSX.Element {
        return renderActions(original, onAction);
      },
    },
  ];
}

export default formColumns;
